import { IconMessage } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useUserLocale } from "@/hooks/useUserLocale";
import { useEventTimezone } from "@/providers/EventProvider";
import { safeDisplayTimeZone } from "@/lib/utils/datetime-tz";
import { smsSkipReasonKey } from "@/lib/utils/smsSkipReason";
import { smsFailReasonKey } from "@/lib/utils/smsFailReason";
import type { SentSms } from "@/types/SentSms";

/**
 * One row of the guest's SMS history — the sent-mail twin (`SentMailEntry`),
 * minus the preview: the rendered body is not stored (plan decision 8), so
 * there is nothing to open.
 *
 * Shows the status chip, the billed parts, the number as the backend sent it
 * (masked when the viewer may not see the guest's numbers — never unmasked
 * here) and, on a skipped / failed row, the reason as its own line (a title
 * is invisible on touch). The time is the EVENT's, like every sibling list.
 *
 * `useSentSmsDisplay` is the same reading of a row for the event's "Sent SMS"
 * table (SMS plugin wave, F-5), so the two lists never word a row apart.
 */
const STATUS_BADGE_COLORS: Record<string, string> = {
  SENT: "user-icon-font-2 outline-user-icon-2 user-icon-bg-2",
  // A test run (consumer `dryrun`): nothing went out, nothing was charged.
  DRYRUN: "user-icon-font-9 outline-user-icon-9 user-icon-bg-9",
  PENDING: "user-icon-font-9 outline-user-icon-9 user-icon-bg-9",
  FAILED: "user-icon-font-12 outline-user-icon-12 user-icon-bg-12",
  SKIPPED: "user-icon-font-17 outline-user-icon-17 user-icon-bg-17",
};

const KNOWN_STATUSES = new Set(Object.keys(STATUS_BADGE_COLORS));

function formatWhen(iso: string | null | undefined, locale: string, timeZone: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    }).format(d);
  } catch {
    return iso;
  }
}

/**
 * The manual send's own fallback codes (main-consumer
 * `src/handlers/sms-manual.ts` `reasonCode(…, 'sms-failed')` for a failure and
 * `reasonCode(…, 'unknown')` for a skip): the dispatcher answered without a
 * code. They are no reason, so never "Failed – code sms-failed" (audit r2
 * R2-FE-7): a row's status chip already says "Failed" / "Skipped", a count
 * says the plain `generic` words. Kept out of the reason maps on purpose —
 * those MIRROR the consumer's reason lists (drift gate).
 */
const SMS_FALLBACK_FAIL = "sms-failed";
const SMS_FALLBACK_SKIP = "unknown";
const isSmsFallbackCode = (code: string) => code === SMS_FALLBACK_FAIL || code === SMS_FALLBACK_SKIP;

/**
 * The words of a stored skip / fail code (F1a's maps, MIRROR of the consumer's
 * `sent-sms-repo.ts` lists). A code this build does not know is data: the
 * generic `unknown` line carries it raw. No code — or one of the manual
 * send's fallbacks — no line.
 */
function reasonWords(
  code: string | null | undefined,
  keyOf: (code: string) => string | null,
  t: (key: string, values?: Record<string, string>) => string,
): string | null {
  if (typeof code !== "string") return null;
  const trimmed = code.trim();
  if (trimmed === "" || isSmsFallbackCode(trimmed)) return null;
  const key = keyOf(trimmed);
  return key ? t(key) : t("unknown", { code: trimmed });
}

/**
 * The words of a "not sent" code — a row's `skipReason`, a manual send's skip
 * entry or skip-count key (SMS manual send, F-4). A skip code first; else a
 * fail / run-failure code, because a manual send's skip list also carries the
 * dispatcher's refusals before any row (`sms-unclaimable`,
 * `sms_account_blocked`, …); else the generic skip line with the raw code.
 * The manual send's fallbacks are plain words without a code: `sms-failed`
 * "Failed", `unknown` "Not sent" (R2-FE-7).
 */
export function smsNotSentWords(
  code: string | null | undefined,
  tSkip: (key: string, values?: Record<string, string>) => string,
  tFail: (key: string, values?: Record<string, string>) => string,
): string | null {
  if (typeof code !== "string") return null;
  const trimmed = code.trim();
  if (trimmed === "") return null;
  if (trimmed === SMS_FALLBACK_FAIL) return tFail("generic");
  if (trimmed === SMS_FALLBACK_SKIP) return tSkip("generic");
  const skip = smsSkipReasonKey(trimmed);
  if (skip) return tSkip(skip);
  const fail = smsFailReasonKey(trimmed);
  if (fail) return tFail(fail);
  return tSkip("unknown", { code: trimmed });
}

/** What a row says: status chip, reason line, billed parts and time. */
export function useSentSmsDisplay(sms: SentSms) {
  const t = useTranslations("smsGuest");
  const locale = useUserLocale();
  const timeZone = safeDisplayTimeZone(useEventTimezone());
  const status = typeof sms.status === "string" ? sms.status : "";
  const known = KNOWN_STATUSES.has(status);
  const tSkip = useTranslations("smsSkipReason");
  const tFail = useTranslations("smsFailReason");
  // A test-mode row names the refusal the provider would have given, or —
  // without one — the skip the send would have taken (the day counter, the
  // provider's opt-out list; audit r1 U9).
  const dryrunFail = status === "DRYRUN" ? reasonWords(sms.failReason, smsFailReasonKey, tFail) : null;
  const reason =
    status === "SKIPPED"
      ? typeof sms.skipReason === "string" && isSmsFallbackCode(sms.skipReason.trim())
        ? null
        : smsNotSentWords(sms.skipReason, tSkip, tFail)
      : status === "FAILED"
        ? reasonWords(sms.failReason, smsFailReasonKey, tFail)
        : status === "DRYRUN"
          ? (dryrunFail ?? reasonWords(sms.skipReason, smsSkipReasonKey, tSkip))
          : null;
  const parts = typeof sms.parts === "number" && sms.parts > 0 ? sms.parts : null;
  return {
    status,
    // An unknown status is data, shown as sent rather than guessed at.
    statusLabel: known ? t(`status.${status}`) : status,
    statusClass: STATUS_BADGE_COLORS[status] ?? "",
    reason,
    partsLabel: parts !== null ? t("parts", { count: parts }) : "",
    when: sms.createdAt ? formatWhen(sms.createdAt, locale, timeZone) : "",
  };
}

export const SentSmsEntry: React.FC<{ sms: SentSms }> = ({ sms }) => {
  const { status, statusLabel, statusClass, reason, partsLabel, when } = useSentSmsDisplay(sms);

  return (
    <li
      className="rounded-xl px-3 py-2.5 border flex flex-col gap-1 outline-tertiary"
      data-testid="sent-sms-entry"
    >
      <div className="flex flex-row items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <IconMessage size={14} className="font-secondary shrink-0" />
          <p className="body-3-medium truncate" data-testid="sent-sms-to">
            {sms.to || "—"}
          </p>
        </div>
        {status && (
          <span
            data-testid="sent-sms-status"
            className={`body-4-regular shrink-0 px-2 py-0.5 border rounded-lg ${statusClass}`}
          >
            {statusLabel}
          </span>
        )}
      </div>
      {reason && (
        <p data-testid="sent-sms-reason" className="body-4-regular font-tertiary break-words">
          {reason}
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        <p className="body-4-regular font-tertiary truncate" data-testid="sent-sms-parts">
          {partsLabel}
        </p>
        {when && <p className="body-4-regular font-tertiary shrink-0">{when}</p>}
      </div>
    </li>
  );
};
